import { z } from 'zod';
import {
  audienceDigestSchema,
  audienceIdSchema,
  audienceProposalSchema,
  audienceRuleSchema,
  nativeAudienceDefinitionSchema,
} from '@charitystack/schemas';
export * from './navigation.js';

export const assistantEntryRoleSchema = z.enum(['user', 'assistant', 'action', 'error', 'system']);

export const assistantEntryKindSchema = z.enum([
  'message',
  'action',
  'error',
  'feedback',
  'system',
]);

export const assistantFeedbackValueSchema = z.enum(['up', 'down']);

export const assistantThreadStatusSchema = z.enum(['open', 'closed']);

export const assistantEntryStatusSchema = z.enum(['pending', 'complete', 'error']);

export const assistantRunStatusSchema = z.enum([
  'queued',
  'dispatched',
  'running',
  'succeeded',
  'failed',
  'cancelled',
]);

/**
 * Client capabilities the assistant reads. keepSavedPeople (ADM-223): the dock
 * reads a keepSavedPeople proposal. ruleChange (ADM-228): the dock reads a
 * proposal card's ruleChange.
 */
export const assistantClientCapabilitySchema = z.enum(['keepSavedPeople', 'ruleChange']);
export type AssistantClientCapability = z.infer<typeof assistantClientCapabilitySchema>;
export const DASHBOARD_ASSISTANT_CLIENT_CAPABILITIES: AssistantClientCapability[] = ['keepSavedPeople', 'ruleChange'];

export const assistantRunContextSchema = z
  .object({
    currentFeature: z.string().max(120).optional(),
    pathname: z.string().max(500).optional(),
    timezone: z.string().max(120).optional(),
    timezoneOffset: z.string().max(40).optional(),
    dashboardCurrentTime: z.string().max(120).optional(),
    // What this dashboard build can render, sent with each run (ADM-223).
    // A tab opened before a capability shipped never sends it, so the
    // assistant keeps the old behaviour for that tab.
    clientCapabilities: z.array(assistantClientCapabilitySchema).max(16).optional(),
  })
  .strict()
  .default({});

// Browser-safe projection. Worker-only fields such as Clerk session IDs are
// deliberately excluded and must never be returned by tRPC.
export const assistantRunSchema = z
  .object({
    runId: z.string().min(1),
    threadId: z.string().min(1),
    clientEntryId: z.string().min(1),
    organizationId: z.string().min(1),
    status: assistantRunStatusSchema,
    executorVersion: z.string().min(1),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    dispatchedAt: z.string().datetime().optional(),
    startedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
    cancelledAt: z.string().datetime().optional(),
    failureCategory: z.string().max(120).optional(),
    failureMessage: z.string().max(500).optional(),
    threadReadyForNewRun: z.boolean().optional(),
    inngestEventId: z.string().min(1).optional(),
    inngestRunId: z.string().min(1).optional(),
  })
  .strict();

export const startAssistantRunInputSchema = z
  .object({
    threadId: z.string().min(1),
    clientEntryId: z.string().min(1),
    organizationId: z.string().min(1),
    content: z.string().trim().min(1).max(20000),
    context: assistantRunContextSchema,
  })
  .strict();

export const getAssistantRunInputSchema = z
  .object({
    runId: z.string().min(1),
    organizationId: z.string().min(1),
  })
  .strict();

export const getActiveAssistantRunInputSchema = z
  .object({
    threadId: z.string().min(1),
    organizationId: z.string().min(1),
  })
  .strict();

export const cancelAssistantRunInputSchema = getAssistantRunInputSchema;

export const assistantDisplayPayloadSchema = z
  .object({
    renderer: z.string().min(1),
    summary: z.string().max(500).optional(),
    data: z.record(z.unknown()).default({}),
  })
  .strict();

// A model may propose an audience, but it never supplies trusted actor,
// organization, session, revision, expiry, or digest bindings. The server
// adds those fields after validating the definition against Marketing's live
// catalog. The dashboard confirms the persisted envelope and performs the
// eventual Marketing mutation as the signed-in user.
export const assistantAudienceDraftInputSchema = z
  .object({
    segmentId: audienceIdSchema.optional(),
    selectionToken: z.string().min(1).max(256).optional(),
    name: z.string().trim().min(1).max(120),
    description: z.string().max(2000).default(''),
    definition: nativeAudienceDefinitionSchema,
    explanation: z.string().trim().min(1).max(4000),
  })
  .strict();

export const assistantAudienceProposalDataSchema = z
  .object({
    version: z.literal(1),
    sessionId: z.string().min(1).max(256),
    proposalHash: audienceDigestSchema,
    proposal: audienceProposalSchema,
    unavailableRequests: z.array(z.string().min(1).max(500)).max(10).default([]),
    // An update's rule change against the saved segment (ADM-228). When both
    // rules are flat lists with the same combinator, each saved condition the
    // draft no longer has, and each condition it adds. Otherwise only
    // groupingChanged, with both lists empty. Display only: never saved.
    ruleChange: z
      .object({
        removed: z.array(audienceRuleSchema).max(100),
        added: z.array(audienceRuleSchema).max(100),
        groupingChanged: z.literal(true).optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.proposal.sourceSelection?.threadId !== undefined &&
      value.proposal.sourceSelection.threadId !== value.sessionId) {
      ctx.addIssue({
        code: 'custom',
        path: ['proposal', 'sourceSelection', 'threadId'],
        message: 'The reviewed selection must belong to the proposal chat session',
      });
    }
  });

export const assistantAudienceProposalDisplayPayloadSchema = assistantDisplayPayloadSchema.extend({
  renderer: z.literal('audience_proposal'),
  data: assistantAudienceProposalDataSchema,
});

/**
 * Which segment a proposal card is for (ADM-228): a saved segment by its id, a
 * new one by its name with only case and whitespace normalised. Every other
 * character counts, so "Donors > $100" and "Donors < $100" are two segments.
 * In one reply, a later card with the same key replaces the earlier one.
 */
export function audienceProposalCardKey(proposal: { segmentId?: string; name: string }): string {
  return proposal.segmentId
    ? `segment:${proposal.segmentId}`
    : `new:${proposal.name.toLocaleLowerCase().replace(/\s+/g, ' ').trim()}`;
}

export type AssistantAudienceDraftInput = z.infer<typeof assistantAudienceDraftInputSchema>;
export type AssistantAudienceProposalData = z.infer<typeof assistantAudienceProposalDataSchema>;

export const dashboardAnalyticsDatasetSchema = z.enum(['payments']);

export const dashboardAnalyticsMetricSchema = z.enum([
  'revenue',
  'payment_count',
  'average_payment',
  'recurring_share',
  'top_forms',
  'top_funds',
  'top_contacts',
  'top_tickets',
  'status_breakdown',
  'payment_method_breakdown',
  'checkout_bump_amount',
  'checkout_bump_attach_rate',
  'monthly_bump_conversions',
  'monthly_bump_conversion_rate',
  'issue_trend',
  'form_type_breakdown',
  'utm_breakdown',
]);

export const dashboardAnalyticsDateRangeSchema = z
  .object({
    preset: z
      .enum([
        'last_7_days',
        'last_30_days',
        'last_90_days',
        'this_month',
        'last_month',
        'current_year',
        'year_to_date',
        'last_12_months',
        'all_time',
      ])
      .optional(),
    startDate: z.string().max(40).optional(),
    endDate: z.string().max(40).optional(),
  })
  .strict()
  .default({ preset: 'last_30_days' });

export const dashboardAnalyticsToolInputSchema = z
  .object({
    dataset: dashboardAnalyticsDatasetSchema.default('payments'),
    metric: dashboardAnalyticsMetricSchema,
    dateRange: dashboardAnalyticsDateRangeSchema,
    grain: z.enum(['day', 'week', 'month']).optional(),
    filters: z
      .object({
        status: z
          .array(z.enum(['success', 'failed', 'pending', 'refunded', 'disputed']))
          .optional(),
        frequency: z
          .array(z.enum(['one_time', 'recurring', 'daily', 'weekly', 'monthly', 'annual']))
          .optional(),
        formNames: z.array(z.string().min(1).max(120)).optional(),
        fundNames: z.array(z.string().min(1).max(120)).optional(),
        formTypes: z.array(z.string().min(1).max(80)).optional(),
        paymentMethods: z.array(z.string().min(1).max(80)).optional(),
        ticketNames: z.array(z.string().min(1).max(120)).optional(),
        utmParameters: z.array(z.string().min(1).max(160)).optional(),
      })
      .strict()
      .default({}),
    visualization: z.enum(['auto', 'line', 'bar', 'pie', 'table', 'kpi']).default('auto'),
    limit: z.number().int().min(1).default(10),
  })
  .strict();

export const dashboardAnalyticsSeriesSchema = z
  .object({
    key: z.string().min(1).max(80),
    label: z.string().min(1).max(120),
    format: z.enum(['currency', 'number', 'percent']).default('number'),
  })
  .strict();

export const dashboardAnalyticsVisualizationSchema = z
  .object({
    type: z.enum(['line', 'bar', 'pie', 'table', 'kpi']),
    xKey: z.string().min(1).max(80).optional(),
    series: z.array(dashboardAnalyticsSeriesSchema).default([]),
  })
  .strict();

export const dashboardAnalyticsResultDataSchema = z
  .object({
    version: z.union([z.literal(1), z.literal(2)]),
    title: z.string().min(1).max(160),
    subtitle: z.string().max(220).optional(),
    visualization: dashboardAnalyticsVisualizationSchema,
    rows: z.array(z.record(z.union([z.string(), z.number(), z.boolean(), z.null()]))),
    insights: z.array(z.string().max(240)).max(4).default([]),
    caveats: z.array(z.string().max(240)).max(4).default([]),
    query: z
      .object({
        dataset: dashboardAnalyticsDatasetSchema,
        metric: dashboardAnalyticsMetricSchema,
        dateRangeLabel: z.string().max(120),
        grain: z.enum(['day', 'week', 'month']).optional(),
        organizationName: z.string().max(160).optional(),
      })
      .strict(),
  })
  .strict();

export const dashboardAnalyticsDisplayPayloadSchema = assistantDisplayPayloadSchema.extend({
  renderer: z.literal('dashboard_analytics_result'),
  data: dashboardAnalyticsResultDataSchema,
});

// AI Tag Automation (initiatives/ai-tag-automation) — `resolve_contact_selection`
// server tool. The tool maps these criteria to the opensearch-integration-backend
// contacts query (Phase 1 keys), returns a count + sample, and materializes the
// matching contact-id set keyed by an opaque selectionToken for the (Phase 3)
// apply job. NOTE: a PocketFlows "Segment" is a distinct persisted marketing
// entity — this is an ad-hoc contact *selection*, never a PF Segment.
const contactTagSchema = z.string().min(1).max(50);
// trim() before min(1) so a whitespace-only value fails validation at the
// tool call (model retries with clean input) instead of resolving to a
// confusing zero-match selection.
const contactGeoValueSchema = z.string().trim().min(1).max(80);

export const contactSelectionCriteriaSchema = z
  .object({
    // Lifetime totals (contact-index totalTransactionValue).
    lifetimeAmountGte: z.number().nonnegative().optional(),
    lifetimeAmountLte: z.number().nonnegative().optional(),
    // Per-period donation behavior (transaction-index composite agg). `years`
    // scopes the period; omit for all-time. Count/amount thresholds are the
    // Phase 1 post-filters ("donated N times", "gave < $X this year").
    years: z
      .array(z.string().regex(/^\d{4}$/))
      .max(20)
      .optional(),
    donationCountGte: z.number().int().nonnegative().optional(),
    donationCountLte: z.number().int().nonnegative().optional(),
    periodAmountGte: z.number().nonnegative().optional(),
    periodAmountLte: z.number().nonnegative().optional(),
    taxDeductible: z.array(z.enum(['taxDeductible', 'nonTaxDeductible'])).optional(),
    // Tag-state predicates (Phase 1). hasAnyTags = OR, hasAllTags = AND,
    // missingTags = NOT.
    hasAllTags: z.array(contactTagSchema).max(50).optional(),
    hasAnyTags: z.array(contactTagSchema).max(50).optional(),
    missingTags: z.array(contactTagSchema).max(50).optional(),
    names: z.array(z.string().min(1).max(160)).max(100).optional(),
    // Geolocation predicates (PE-1879). Values are the user's wording
    // ("Texas", "United States"); the backend matches them case-insensitively
    // against the org's real address values (contactGeoOptions) and expands to
    // every raw casing variant before querying — `.keyword` matching is
    // case-sensitive until the PE-1876 standardization backfill. A contact
    // matches when ANY of its addresses matches (same semantics as the
    // dashboard Location filter). Contacts with no address data never match.
    // notCountries is the only supported negation: it resolves to the
    // complement of the org's KNOWN countries, so blank-country contacts are
    // NOT included in "outside the US".
    countries: z.array(contactGeoValueSchema).max(20).optional(),
    notCountries: z.array(contactGeoValueSchema).max(20).optional(),
    states: z.array(contactGeoValueSchema).max(30).optional(),
    cities: z.array(contactGeoValueSchema).max(50).optional(),
  })
  .strict();

// What the selection is for, so the card and the reply only offer tagging when
// tagging was asked for: `tag` (tag or untag them), `lookup` (just show who
// matches) or `segment` (the reviewed list of a fixed or hybrid segment).
// Optional: a missing purpose means `tag`, which is how every selection made
// before this field existed, and every one from an older assistant, behaves.
export const contactSelectionPurposeSchema = z.enum(['tag', 'lookup', 'segment']);

export const resolveContactSelectionInputSchema = z
  .object({
    criteria: contactSelectionCriteriaSchema,
    // When the request implies a NEW tag concept (e.g. "tag donors who gave 3+
    // times" → no such tag exists yet), the model proposes 1-3 concise names
    // here. The preview card shows them as clickable chips (and pre-fills the
    // create-tag field with the first); the admin picks one, edits it, or types
    // their own — and confirms creation. Omit when the user means an existing tag.
    suggestedTagNames: z.array(z.string().min(1).max(50)).max(3).optional(),
    purpose: contactSelectionPurposeSchema.optional(),
  })
  .strict();

export const contactSelectionSampleContactSchema = z
  .object({
    id: z.string().min(1),
    fullName: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    // "city, region, country" of the address that matched the geo criteria
    // (else the primary/first address on file) — PE-1948.
    location: z.string().optional(),
    lifetimeAmount: z.number().optional(),
    periodCount: z.number().optional(),
    periodAmount: z.number().optional(),
  })
  .strict();

export const contactSelectionPreviewDataSchema = z
  .object({
    version: z.literal(1),
    matchedCount: z.number().int().nonnegative(),
    sampleContacts: z.array(contactSelectionSampleContactSchema).max(50).default([]),
    // Human-readable restatement of the resolved criteria for the PreviewCard.
    criteriaEcho: z.string().min(1).max(500),
    // Opaque pointer to the materialized id set. Absent when matchedCount is 0
    // or when overCap is true (apply is blocked, so nothing is materialized).
    selectionToken: z.string().min(1).optional(),
    // True when matchedCount exceeds the v1 apply cap; the preview still shows
    // the count, but the selection is not materialized and apply is blocked.
    overCap: z.boolean().default(false),
    cap: z.number().int().positive(),
    // Model-proposed names (1-3) for a NEW tag when the request implied one.
    // The card renders them as clickable chips and pre-fills the create-tag
    // field with the first.
    suggestedTagNames: z.array(z.string().min(1).max(50)).max(3).optional(),
    // Missing means `tag` (see contactSelectionPurposeSchema).
    purpose: contactSelectionPurposeSchema.optional(),
    warnings: z.array(z.string().max(240)).max(4).default([]),
  })
  .strict();

export const contactSelectionPreviewDisplayPayloadSchema = assistantDisplayPayloadSchema.extend({
  renderer: z.literal('contact_selection_preview'),
  data: contactSelectionPreviewDataSchema,
});

// `apply_contact_tags` server tool — the chat-driven apply path. Operates on a
// selection previously materialized by `resolve_contact_selection` (the model
// must have shown the preview and received the user's confirmation in chat).
// Tags are written through the same org-scoped /contacts/tags endpoint the
// PreviewCard uses; the org tag dictionary is extended via /org-contact-tags
// when `createIfMissing` is set.
export const applyContactTagsInputSchema = z
  .object({
    // Token from the resolve tool's result. Optional: when omitted the backend
    // falls back to the thread's most recently resolved selection (model
    // history replays message entries only, so the token is often not visible
    // to the model on the confirmation turn).
    selectionToken: z.string().min(1).optional(),
    action: z.enum(['add', 'remove']).default('add'),
    tagNames: z.array(contactTagSchema).min(1).max(5),
    // Create tags that are not in the org dictionary yet (add only). When
    // false/omitted and a tag is missing, the tool returns needs_confirmation
    // instead of writing anything.
    createIfMissing: z.boolean().optional(),
  })
  .strict();

// Rendered success card for a completed chat-driven apply. The frontend also
// uses it to refresh the org tag dictionary UI (a created tag / changed usage
// counts should appear in Settings → Tags without a reload).
export const contactTagsApplyResultDataSchema = z
  .object({
    version: z.literal(1),
    action: z.enum(['add', 'remove']),
    tags: z.array(contactTagSchema).min(1).max(5),
    createdTags: z.array(contactTagSchema).max(5).default([]),
    updated: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    excludedCount: z.number().int().nonnegative().default(0),
    criteriaEcho: z.string().max(500),
    appliedAtIso: z.string(),
  })
  .strict();

export const contactTagsApplyResultDisplayPayloadSchema = assistantDisplayPayloadSchema.extend({
  renderer: z.literal('contact_tags_apply_result'),
  data: contactTagsApplyResultDataSchema,
});

export const assistantEntrySchema = z.object({
  threadId: z.string().min(1),
  entryId: z.string().min(1),
  clientEntryId: z.string().min(1),
  organizationId: z.string().min(1),
  userId: z.string().min(1),
  role: assistantEntryRoleSchema,
  kind: assistantEntryKindSchema,
  content: z.string().default(''),
  createdAt: z.string().datetime(),
  sequence: z.number().int().positive().optional(),
  turnId: z.string().min(1).optional(),
  runId: z.string().min(1).optional(),
  parentEntryId: z.string().min(1).optional(),
  status: assistantEntryStatusSchema.optional(),
  displayPayload: assistantDisplayPayloadSchema.optional(),
  redacted: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const assistantThreadSummarySchema = z.object({
  threadId: z.string().min(1),
  userId: z.string().min(1),
  organizationId: z.string().min(1),
  title: z.string().min(1),
  status: assistantThreadStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  lastEntryAt: z.string().datetime().optional(),
  closedAt: z.string().datetime().optional(),
  deletedAt: z.string().datetime().optional(),
  entryCount: z.number().int().nonnegative(),
  nextSequence: z.number().int().positive().optional(),
  messageEntryCount: z.number().int().nonnegative().default(0),
  actionEntryCount: z.number().int().nonnegative().default(0),
  hasActionOnlyEntries: z.boolean().default(false),
});

export const appendEntryInputSchema = z.object({
  threadId: z.string().min(1).optional(),
  clientEntryId: z.string().min(1),
  organizationId: z.string().min(1),
  role: assistantEntryRoleSchema,
  kind: assistantEntryKindSchema,
  content: z.string().max(20000).default(''),
  turnId: z.string().min(1).optional(),
  runId: z.string().min(1).optional(),
  parentEntryId: z.string().min(1).optional(),
  status: assistantEntryStatusSchema.optional(),
  displayPayload: assistantDisplayPayloadSchema.optional(),
  metadata: z.record(z.unknown()).optional(),
  createdAt: z.string().datetime().optional(),
});

export const appendEntryOutputSchema = z.object({
  thread: assistantThreadSummarySchema,
  entry: assistantEntrySchema,
  idempotentReplay: z.boolean(),
});

export const listThreadsInputSchema = z.object({
  organizationId: z.string().min(1),
  limit: z.number().int().min(1).max(100).default(30),
  cursor: z.string().optional(),
});

export const listThreadsOutputSchema = z.object({
  threads: z.array(assistantThreadSummarySchema),
  nextCursor: z.string().optional(),
});

export const getThreadInputSchema = z.object({
  threadId: z.string().min(1),
  organizationId: z.string().min(1),
  limit: z.number().int().min(1).max(200).default(100),
  cursor: z.string().optional(),
  sinceSequence: z.number().int().nonnegative().optional(),
  allowMissing: z.boolean().optional(),
  // What the reading tab can render (ADM-228): saved cards drop fields it
  // didn't declare. A tab from before this field never sends it.
  clientCapabilities: z.array(assistantClientCapabilitySchema).max(16).optional(),
});

export const getThreadOutputSchema = z.object({
  thread: assistantThreadSummarySchema,
  entries: z.array(assistantEntrySchema),
  nextCursor: z.string().optional(),
});

export const closeThreadInputSchema = z.object({
  threadId: z.string().min(1),
  organizationId: z.string().min(1),
});

export const feedbackSetInputSchema = z.object({
  threadId: z.string().min(1),
  entryId: z.string().min(1),
  organizationId: z.string().min(1),
  feedback: assistantFeedbackValueSchema,
});

export type AssistantEntryRole = z.infer<typeof assistantEntryRoleSchema>;
export type AssistantEntryKind = z.infer<typeof assistantEntryKindSchema>;
export type AssistantEntryStatus = z.infer<typeof assistantEntryStatusSchema>;
export type AssistantRunStatus = z.infer<typeof assistantRunStatusSchema>;
export type AssistantRunContext = z.infer<typeof assistantRunContextSchema>;
export type AssistantRun = z.infer<typeof assistantRunSchema>;
export type StartAssistantRunInput = z.infer<typeof startAssistantRunInputSchema>;
export type GetAssistantRunInput = z.infer<typeof getAssistantRunInputSchema>;
export type GetActiveAssistantRunInput = z.infer<typeof getActiveAssistantRunInputSchema>;
export type CancelAssistantRunInput = z.infer<typeof cancelAssistantRunInputSchema>;
export type AssistantDisplayPayload = z.infer<typeof assistantDisplayPayloadSchema>;
export type DashboardAnalyticsToolInput = z.infer<typeof dashboardAnalyticsToolInputSchema>;
export type DashboardAnalyticsDisplayPayload = z.infer<
  typeof dashboardAnalyticsDisplayPayloadSchema
>;
export type DashboardAnalyticsResultData = z.infer<typeof dashboardAnalyticsResultDataSchema>;
export type ContactSelectionCriteria = z.infer<typeof contactSelectionCriteriaSchema>;
export type ContactSelectionPurpose = z.infer<typeof contactSelectionPurposeSchema>;
export type ResolveContactSelectionInput = z.infer<typeof resolveContactSelectionInputSchema>;
export type ApplyContactTagsInput = z.infer<typeof applyContactTagsInputSchema>;
export type ContactTagsApplyResultData = z.infer<typeof contactTagsApplyResultDataSchema>;
export type ContactTagsApplyResultDisplayPayload = z.infer<
  typeof contactTagsApplyResultDisplayPayloadSchema
>;
export type ContactSelectionSampleContact = z.infer<typeof contactSelectionSampleContactSchema>;
export type ContactSelectionPreviewData = z.infer<typeof contactSelectionPreviewDataSchema>;
export type ContactSelectionPreviewDisplayPayload = z.infer<
  typeof contactSelectionPreviewDisplayPayloadSchema
>;
export type AssistantEntry = z.infer<typeof assistantEntrySchema>;
export type AssistantThreadSummary = z.infer<typeof assistantThreadSummarySchema>;
export type AppendEntryInput = z.infer<typeof appendEntryInputSchema>;
export type AppendEntryOutput = z.infer<typeof appendEntryOutputSchema>;
export type ListThreadsInput = z.infer<typeof listThreadsInputSchema>;
export type ListThreadsOutput = z.infer<typeof listThreadsOutputSchema>;
export type GetThreadInput = z.infer<typeof getThreadInputSchema>;
export type GetThreadOutput = z.infer<typeof getThreadOutputSchema>;
export type CloseThreadInput = z.infer<typeof closeThreadInputSchema>;
export type FeedbackSetInput = z.infer<typeof feedbackSetInputSchema>;
